/**
 * Errors caused by ad blockers, offline users, private browsing, or missing optional
 * server config — not application bugs. Call sites should degrade gracefully and
 * avoid reporting these to Sentry.
 */
export function getErrorMessageFromUnknown(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function normalizeErrorMessageForMatch(message: string): string {
  return message.replace(/^[A-Za-z]+Error:\s*/, '').trim();
}

export function isExpectedEnvironmentErrorMessage(message: string): boolean {
  const normalized = normalizeErrorMessageForMatch(message.trim());
  if (!normalized) {
    return false;
  }

  if (normalized.includes('Unreachable hosts')) {
    return true;
  }
  if (normalized.includes('alg.li/support-unreachable-hosts')) {
    return true;
  }

  // Some browsers/call sites append extra context after these generic network-error strings
  // (e.g. a caller building "Error fetching X: Failed to fetch", or Chrome annotating a
  // blocked-by-client fetch with the target host as "Failed to fetch (www.example.com)") — match
  // as a substring rather than requiring the whole normalized message to equal it exactly, or
  // real environmental noise silently stops being recognized (see JOBCRAWLS-KAV).
  if (normalized.includes('Failed to fetch dynamically imported module')) {
    return true;
  }
  if (normalized.includes('Failed to fetch')) {
    return true;
  }
  if (normalized.includes('fetch failed')) {
    return true;
  }
  if (normalized.includes('Load failed')) {
    return true;
  }
  if (normalized.includes('NetworkError when attempting to fetch resource.')) {
    return true;
  }
  if (normalized.includes('Network request failed')) {
    return true;
  }
  // undici (Node's fetch) surfaces a remote server closing the connection mid-response as these
  // two distinct shapes rather than the browser-style "fetch failed" — seen server-side fetching
  // third-party company logo/hero images for dimension checks, where the remote host is outside
  // our control (JOBCRAWLS-V2F). "terminated" alone is generic enough elsewhere that it's matched
  // exactly rather than as a substring, to avoid masking an unrelated real error that happens to
  // contain the word.
  if (normalized === 'terminated') {
    return true;
  }
  if (normalized.includes('other side closed')) {
    return true;
  }

  if (normalized.includes('Terrain and hillshade are disabled')) {
    return true;
  }
  if (normalized.includes('Canvas2D limitations when fingerprinting protection')) {
    return true;
  }

  if (normalized === 'OPENAI_API_KEY is not set') {
    return true;
  }

  return false;
}

export function isExpectedEnvironmentError(error: unknown): boolean {
  return isExpectedEnvironmentErrorMessage(getErrorMessageFromUnknown(error));
}
